import { getTailwindColor } from '@/utils/colors';
import clsx from 'clsx';

export type ListProps = {
    content: {
        innerHTML: TrustedHTML;
        innerContent: [TrustedHTML];
    }[];
    index: number;
    hash: string;
    ordered?: boolean;
    useDottedUnorderedList?: boolean;
    useStandardStyle?: boolean;
    useCheckMarkStyle?: boolean;
    textColor?: string;
    unorderedListColor?:
        | {
              color: string;
              name: string;
          }
        | string;
    backgroundColor?: string;
};

export const List = ({ content, ...props }: ListProps) => {
    // Backwards compat: If you resave a page, this will be updated
    // to the new markup where content is an array of <li />
    if (typeof content === 'string') {
        return <div dangerouslySetInnerHTML={{ __html: content }} />;
    }

    if (!Array.isArray(content)) {
        return null;
    }

    const ListTag = props.ordered ? 'ol' : 'ul';
    const classNames = clsx(
        'styled-link r-relative r-mb-4',
        getTailwindColor(props.textColor, 'text'),
        getTailwindColor(props.backgroundColor, 'bg'),
        getTailwindColor(props.unorderedListColor || 'black', 'border'),
        {
            'r-border-l-4 r-pl-8 r-ml-8': !props.useStandardStyle && !props.useDottedUnorderedList && !props.useCheckMarkStyle,
            'r-list-disc r-pl-4 ': props.useStandardStyle && !props.ordered,
            'r-list-image-none': props.useCheckMarkStyle && !props.ordered,
            'r-list-decimal r-pl-4': props.ordered,
        }
    );

    return (
        <ListTag className={classNames}>
            {content.map(({ innerHTML }, index) => {
                const matches = innerHTML.toString().match(/<li>(.*?)<\/li>/);
                if (!Array.isArray(matches) || !matches[1]) {
                    return null;
                }

                return (
                    <li
                        key={index}
                        className={clsx({
                            'r-pb-2': props.useCheckMarkStyle && !props.ordered,
                            'r-flex r-items-center r-gap-3 r-pl-0': !props.useStandardStyle && !props.ordered,
                        })}
                    >
                        {props.useDottedUnorderedList && !props.ordered ? (
                            <span className={clsx(props.unorderedListColor ? getTailwindColor(props.unorderedListColor, 'text') : '')}>&mdash;</span>
                        ) : null}
                        {props.useCheckMarkStyle && !props.ordered ? (
                            <svg width="22" height="16" viewBox="0 0 22 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path
                                    className={clsx(getTailwindColor(props.unorderedListColor || 'listCheck', 'fill'))}
                                    d="M21.5 2.0564C21.5 2.44021 21.3535 2.82421 21.0605 3.11671L9.06055 15.1167C8.76875 15.4111 8.38438 15.5564 8 15.5564C7.61562 15.5564 7.23219 15.4099 6.93969 15.1169L0.939688 9.11694C0.646719 8.82515 0.5 8.44077 0.5 8.0564C0.5 7.19952 1.20078 6.5564 2 6.5564C2.38381 6.5564 2.76781 6.70288 3.06031 6.99585L8 11.9376L18.9406 0.997022C19.2312 0.703115 19.6156 0.556396 20 0.556396C20.8016 0.556396 21.5 1.19858 21.5 2.0564Z"
                                />
                            </svg>
                        ) : null}
                        <span dangerouslySetInnerHTML={{ __html: matches[1] }} />
                    </li>
                );
            })}
        </ListTag>
    );
};
